import * as React from "react";
import {cx} from "emotion";

import "./select-list-section.scss";

import {cs}             from "@Common/react/chain-services";
import {consumeContext} from "@Common/react/context";
import {Load2}          from "@Common/react/load2";
import {UseState}       from "@Common/react/use-state";
import {sum}            from "@Common/utils/collections";

import {formatDate, formatDate1} from "../../../../../../../format-date";
import {ErrorHighlighter}        from "../../../../../../../light/error-highlighter";
import {Checkbox}                from "../../../../../../common/design2/checkbox/checkbox";
import {DataTableD1}             from "../../../../../../common/design2/data-table/data-table-d1";

export const SelectListSection = ({entityId, next}) => cs(
    ["unselecteds", (_, next) => UseState({next})],
    consumeContext("apis"),
    ["list", ({apis}, next) => Load2({
        fetch: () => apis.next.mailing.crm.getCrmList({entityId}).then((result) => result.crmList),
        next,
    })],
    ["selectedListsError", (_, next) => UseState({next})],
    ["eh", (_, next) => ErrorHighlighter({next})],
    ({list, unselecteds, selectedListsError, eh}) => {
        const required = !list.value?.length || (unselecteds.value?.length === list.value.length);
        const showSelectedListsError = (
            unselecteds.value?.length === (list.value?.length - selectedListsError.value?.selectedIds.length) &&
            selectedListsError.value?.selectedIds.some((id) => !unselecteds.value?.includes(id))
        );
        const isSelected = (r) => !unselecteds.value?.find((id1) => id1 === r.id);
        return next({
            valid: !required,
            selectedListsError,
            highlightErrors: () => {
                eh.start();
            },
            getSelecteds: () => list.value.filter(isSelected).map((r) => ({id: r.id, name: r.name})),
            render: ({showErrors}) => (
                <div className="select-list-section-5v4">
                    <div className="header">
                        <div className="title">
                            Select your CRM Address List
                        </div>
                        <div className="sub-title">
                            Address lists from your CRM system appear below. Select 1 or more lists to address your
                            order with.
                        </div>
                    </div>

                    <div className={cx("box", eh.css)}>
                        {showErrors && (
                            showSelectedListsError ? (
                                <div className="error">
                                    {selectedListsError.value.msg}
                                </div>
                            ) : (
                                required && (
                                    <div className="error">
                                        At least one list must be selected
                                    </div>
                                )
                            )
                        )}
                        {(() => {
                            const hasQuantity = list.value?.[0].quantity != null;
                            const hasDateCreated = list.value?.[0].dateCreated != null;
                            return DataTableD1({
                                darkRows: "even",
                                columns: Columns({
                                    hasQuantity,
                                    hasDateCreated,
                                    masterCheckbox: (() => {
                                        const deselectAll = () => unselecteds.onChange(list.value.map((r) => r.id));
                                        const selectAll = () => unselecteds.onChange([]);
                                        return (
                                            !list.value?.find(isSelected) ? {
                                                checked: false,
                                                xdQaName: "Select All Check",
                                                onClick: selectAll,
                                            } : list.value?.find((r) => !isSelected(r)) ? {
                                                checked: "midway",
                                                xdQaName: "Select All Check",
                                                onClick: selectAll,
                                            } : {
                                                checked: true,
                                                xdQaName: "Select All Check",
                                                onClick: deselectAll,
                                            }
                                        );
                                    })(),
                                    count: list.value && {
                                        selected: list.value.length - (unselecteds.value?.length ?? 0),
                                        total: list.value.length,
                                    },
                                }),
                                rows: list.value?.map((crm) => {
                                    const checked = isSelected(crm);
                                    return ({
                                        columns: [
                                            {
                                                render: () => Checkbox({
                                                    checked,
                                                    xdQaName: crm.name + " Check",
                                                    onClick: checked ? (
                                                        () => unselecteds.change((_) => [
                                                            ..._ ?? [],
                                                            crm.id,
                                                        ])
                                                    ) : (
                                                        () => unselecteds.change((_) => _.filter((id1) => id1 !== crm.id))
                                                    ),
                                                }),
                                            },
                                            {
                                                render: () => crm.name,
                                                sortValue: () => ("" + crm.name).toLowerCase(),
                                            },
                                            hasQuantity && {
                                                render: () => crm.quantity,
                                                sortValue: () => +crm.quantity,
                                            },
                                            hasDateCreated && {
                                                render: () => crm.dateCreated && formatDate1(new Date(crm.dateCreated)),
                                                sortValue: () => crm.dateCreated,
                                            },
                                        ].filter(v => v),
                                        xdQaName: crm.name,
                                    });
                                }),
                                emptyAction: {
                                    label: list.loading ? "Loading..." : "No CRM lists found",
                                },
                                getFooter: !hasQuantity ? null : () => ({
                                    columns: [
                                        {},
                                        {
                                            className: "label",
                                            render: () => <>
                                                Total Addresses Selected
                                            </>,
                                        },
                                        {
                                            className: "count",
                                            render: () => sum(list.value.filter(isSelected), (r) => +(r.quantity??0)),
                                        },
                                    ],
                                }),
                            });
                        })()}
                    </div>

                </div>
            ),
        });
    },
);

const Columns = ({masterCheckbox, hasQuantity, hasDateCreated, count}) => [
    {
        width: "1%",
        className: "checkbox",
        render: () => Checkbox(masterCheckbox),
    },
    {
        width: "30%",
        label: "ADDRESS LIST NAME",
        className: "name",
        subTitle: count && `(${count.selected} of ${count.total} lists selected)`,
        sortable: {
            defaultDirection: "asc",
        },
    },
    hasQuantity && {
        width: "1%",
        label: "QTY",
        className: "qty",
        sortable: {
            defaultDirection: "desc",
        },
    },
    hasDateCreated && {
        width: "1%",
        label: "CREATED",
        className: "created",
        sortable: {
            defaultDirection: "desc",
        },
    },
].filter(v=>v);
